import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { ComplexRetrievalCallbacks, FtoReportComplexRetrievalRepository, RetrievalTimeCost } from '../../services/complex-retrieval-service';
import { sleep } from '@telecom/shared/utils/task';
import { Parameter, type PatentProfileWithMatchedTechFeature, type RetrievalResult, type TechFeatureSynonym } from '../../types';
import { create, Logger } from '@patsnap/tahiti-core';
import { HUMAN_RETRIEVAL_PER_PATENT_TIME, RETRIEVAL_STRATEGY } from '../../constants';
import _ from 'lodash';
import { I18nService } from '@telecom/shared/services/i18n';
import { FtoReportSearchApi } from '../../apis/search-novelty-api';
import { FtoReportResultSearchApi, type BaseSearchResult, type FeatureKeyword, type FinalResult, type IpcCpcSearch, type SearchNoveltyResultResponse, type SearchStrategy, type SemanticSearch } from '../../apis/search-novelty-result-api';
import { ClaimType, PatentRiskLevel } from '../../enums';

/**
 * 单个检索策略执行状态
 */
const RETRIEVAL_STATUS = {
    /**
     * 运行中
     */
    RUNNING: 'running',
    /**
     * 成功
     */
    SUCCESS: 'success',
};

/**
 * 整体任务状态
 */
const TASK_STATUS = {
    SUCCESS: 'success',
    RUNNING: 'running',
};

/**
 * 再次检索的延迟时间
 */
const SEARCH_DELAY = 1000;

/**
 * 空检索结果
 * @description 用于表示检索结果为空
 */
export const EmptyRetrievalResult: RetrievalResult = Object.freeze({
    idea: '',
    searchExpress: '',
    patents: [],
    strategyParameters: [],
    nextPlan: '',
    finished: false,
});

@injectable()
export class FtoReportComplexRetrievalRepositoryImpl implements FtoReportComplexRetrievalRepository {
    // eslint-disable-next-line max-params
    constructor(
        @inject(FtoReportSearchApi)
        private readonly _searchNoveltyApi: FtoReportSearchApi,
        @inject(FtoReportResultSearchApi)
        private readonly _searchNoveltyResultApi: FtoReportResultSearchApi,
        @inject(I18nService)
        private readonly _i18nService: I18nService,
        @inject(Logger)
        private readonly _logger: Logger,
    ) { }

    terminate(): Promise<void> {
        this._abortController?.abort();
        return Promise.resolve();
    }

    private _abortController: AbortController | null = null;

    async search(taskId: string, callbacks: ComplexRetrievalCallbacks): Promise<void> {
        callbacks.onRetrievalRunning(RETRIEVAL_STRATEGY.SEMANTIC_SEARCH, true);
        callbacks.onRetrievalSuccess(RETRIEVAL_STRATEGY.SEMANTIC_SEARCH, EmptyRetrievalResult);
        await this._searchNoveltyApi.startSearchNovelty({
            task_id: taskId,
        });
        return this._loopSearchNoveltyResult(taskId, callbacks);
    }

    private _validateFeatureKeyword(data: FeatureKeyword): boolean {
        return !!data.tech_feature
            && !!data.synonym
            && !!data.synonym_score
            && Array.isArray(data.words);
    }

    private _convertToTechFeatureSynonym(data: FeatureKeyword[]): TechFeatureSynonym[] {
        return data
            .filter((item) => {
                return this._validateFeatureKeyword(item);
            })
            .map((item) => {
                return {
                    techFeature: item.tech_feature,
                    synonym: item.synonym,
                    synonymScore: item.synonym_score,
                    translation: item.translation,
                    words: item.words.map((word) => ({
                        techFeature: word.tech_feature,
                        synonym: word.synonym,
                        synonymScore: word.synonym_score,
                        translation: word.translation,
                    })),
                };
            });
    }

    private _getRetrievalTimeCost(data: {
        task_cost: number;
        total_comparison_patent_num: number;
    }): RetrievalTimeCost {
        return {
            timeSpend: data.task_cost,
            timeSaved: data.total_comparison_patent_num * HUMAN_RETRIEVAL_PER_PATENT_TIME,
        };
    }

    /**
     * 循环调用检索结果接口，直到检索完成
     */
    private async _loopSearchNoveltyResult(taskId: string, callbacks: ComplexRetrievalCallbacks) {
        let isFinished = false;
        const deferred = create<void>();
        this._abortController = new AbortController();

        while (!isFinished) {
            const data = await this._searchNoveltyResultApi.getSearchNoveltyResult({
                task_id: taskId,
                is_finished: false,
            }, this._abortController.signal);

            if (Array.isArray(data.feature_keywords)) {
                callbacks.onTechFeatureSynonym(this._convertToTechFeatureSynonym(data.feature_keywords));
            }

            // 处理各个检索策略的状态
            this._handleStrategyStatus(data, callbacks);

            if (Array.isArray(data.final_result)) {
                callbacks.onPatentFeatureMatched(this._convertToPatentProfileWithMatchedTechFeature(data.final_result));
            }

            if (_.isNumber(data.task_cost)) {
                callbacks.onRetrievalTime(this._getRetrievalTimeCost(data));
            }

            await sleep(SEARCH_DELAY);

            if (data.task_status !== TASK_STATUS.RUNNING) {
                isFinished = true;
                deferred.resolve();
            }
        }

        return deferred.promise;
    }

    /**
     * 处理各个检索策略的状态
     */
    // eslint-disable-next-line max-lines-per-function
    private _handleStrategyStatus(result: SearchNoveltyResultResponse, callbacks: ComplexRetrievalCallbacks): void {
        // 定义检索策略的处理顺序
        const strategySequence = [
            {
                current: RETRIEVAL_STRATEGY.SEMANTIC_SEARCH,
                next: RETRIEVAL_STRATEGY.IPC_CPC_SEARCH,
                data: result.process_dict?.semantic_search,
            },
            {
                current: RETRIEVAL_STRATEGY.IPC_CPC_SEARCH,
                next: RETRIEVAL_STRATEGY.BLOCK_PROGRESSIVE_SEARCH_1,
                data: result.process_dict?.ipc_cpc_search,
            },
            {
                current: RETRIEVAL_STRATEGY.BLOCK_PROGRESSIVE_SEARCH_1,
                next: RETRIEVAL_STRATEGY.BLOCK_PROGRESSIVE_SEARCH_2,
                data: result.process_dict?.block_progressive_search_1,
            },
            {
                current: RETRIEVAL_STRATEGY.BLOCK_PROGRESSIVE_SEARCH_2,
                next: RETRIEVAL_STRATEGY.BLOCK_PROGRESSIVE_SEARCH_3,
                data: result.process_dict?.block_progressive_search_2,
            },
            {
                current: RETRIEVAL_STRATEGY.BLOCK_PROGRESSIVE_SEARCH_3,
                next: RETRIEVAL_STRATEGY.BLOCK_PROGRESSIVE_SEARCH_4,
                data: result.process_dict?.block_progressive_search_3,
            },
            {
                current: RETRIEVAL_STRATEGY.BLOCK_PROGRESSIVE_SEARCH_4,
                next: RETRIEVAL_STRATEGY.RELEVANT_TRACKING_SEARCH,
                data: result.process_dict?.block_progressive_search_4,
            },
            {
                current: RETRIEVAL_STRATEGY.RELEVANT_TRACKING_SEARCH,
                next: null,
                data: result.process_dict?.relevant_tracking_search,
            },
        ];

        // 处理每个策略的状态
        strategySequence.forEach(strategy => {
            if (!strategy.data) return;

            if (strategy.data.status === RETRIEVAL_STATUS.RUNNING) {
                this._handleRunningStrategy(strategy.current, strategy.data, result, callbacks);
            }

            if (strategy.data.status === RETRIEVAL_STATUS.SUCCESS) {
                this._handleSuccessStrategy(strategy.current, strategy.next, strategy.data, result, callbacks);
            }

            // 如果检索策略未成功且任务已结束，则认为检索策略已成功
            if (strategy.data.status !== RETRIEVAL_STATUS.SUCCESS && result.task_status !== TASK_STATUS.RUNNING) {
                this._handleSuccessStrategy(strategy.current, null, strategy.data, result, callbacks);
            }
        });
    }

    /**
     * 处理正在运行中的检索策略
     */
    // eslint-disable-next-line max-params
    private _handleRunningStrategy(strategyName: RETRIEVAL_STRATEGY, strategyData: BaseSearchResult & { strategy: SearchStrategy<string> }, result: SearchNoveltyResultResponse, callbacks: ComplexRetrievalCallbacks): void {
        callbacks.onRetrievalRunning(strategyName, true);
        callbacks.onRetrievalSuccess(strategyName, this._convertToRetrievalResult(strategyName, strategyData, result));
    }

    /**
     * 处理成功完成的检索策略
     */
    // eslint-disable-next-line max-params
    private _handleSuccessStrategy(
        currentStrategy: RETRIEVAL_STRATEGY,
        nextStrategy: RETRIEVAL_STRATEGY | null,
        strategyData: BaseSearchResult & { strategy: SearchStrategy<string> },
        result: SearchNoveltyResultResponse,
        callbacks: ComplexRetrievalCallbacks,
    ): void {
        callbacks.onRetrievalRunning(currentStrategy, false);
        callbacks.onRetrievalSuccess(currentStrategy, this._convertToRetrievalResult(currentStrategy, strategyData, result));

        // 如果有下一个策略，则启动它
        if (nextStrategy && result.task_status === TASK_STATUS.RUNNING) {
            callbacks.onRetrievalRunning(nextStrategy, true);
            callbacks.onRetrievalSuccess(nextStrategy, EmptyRetrievalResult);
        }
    }

    private _convertToRetrievalResult(name: RETRIEVAL_STRATEGY, data: BaseSearchResult & { strategy: SearchStrategy<string> }, result: SearchNoveltyResultResponse): RetrievalResult {
        // 如果检索策略成功或检索策略未成功但任务已结束，则认为检索策略已成功
        const finished = data.status === RETRIEVAL_STATUS.SUCCESS || (data.status !== RETRIEVAL_STATUS.SUCCESS && result.task_status !== TASK_STATUS.RUNNING);
        const idea = ['cn', 'tw'].includes(this._i18nService.locale.value)
            ? data.strategy.description_zh
            : data.strategy.description_en;
        return {
            idea,
            searchExpress: data.strategy.query,
            patents: data.comparison_result.map((item) => ({
                patentId: item.patent_id,
                score: item.public_score,
            })),
            strategyParameters: this._convertStrategyParameters(name, data.strategy.parameters),
            nextPlan: this._convertNextPlan(name),
            finished,
        };
    }

    // eslint-disable-next-line complexity
    private _convertNextPlan(name: RETRIEVAL_STRATEGY): string {
        if (name === RETRIEVAL_STRATEGY.SEMANTIC_SEARCH) {
            return this._i18nService.t('fto.nextPlan.semanticSearch');
        }
        else if (name === RETRIEVAL_STRATEGY.IPC_CPC_SEARCH) {
            return this._i18nService.t('fto.nextPlan.ipcCpcSearch');
        }
        else if (name === RETRIEVAL_STRATEGY.BLOCK_PROGRESSIVE_SEARCH_1) {
            return this._i18nService.t('fto.nextPlan.blockProgressiveSearch1');
        }
        else if (name === RETRIEVAL_STRATEGY.BLOCK_PROGRESSIVE_SEARCH_2) {
            return this._i18nService.t('fto.nextPlan.blockProgressiveSearch2');
        }
        else if (name === RETRIEVAL_STRATEGY.BLOCK_PROGRESSIVE_SEARCH_3) {
            return this._i18nService.t('fto.nextPlan.blockProgressiveSearch3');
        }
        else if (name === RETRIEVAL_STRATEGY.BLOCK_PROGRESSIVE_SEARCH_4) {
            return this._i18nService.t('fto.nextPlan.blockProgressiveSearch4');
        }
        else if (name === RETRIEVAL_STRATEGY.RELEVANT_TRACKING_SEARCH) {
            return '';
        }
        this._logger.warn(`Unknown strategy: ${name}`);
        return '';
    }

    private _convertStrategyParameters(name: RETRIEVAL_STRATEGY, parameters: unknown): { name: Parameter; value: string[] }[] {
        if (name === RETRIEVAL_STRATEGY.SEMANTIC_SEARCH) {
            const data = parameters as SemanticSearch['strategy']['parameters'];
            return [
                {
                    name: Parameter.FEATURES,
                    value: data.features,
                },
            ];
        }
        else if (name === RETRIEVAL_STRATEGY.IPC_CPC_SEARCH) {
            const data = parameters as IpcCpcSearch['strategy']['parameters'];
            return [
                {
                    name: Parameter.IPC,
                    value: data.ipc,
                },
                {
                    name: Parameter.CPC,
                    value: data.cpc,
                },
            ];
        }
        else {
            return [];
        }
    }

    private _convertToPatentProfileWithMatchedTechFeature(data: FinalResult[]): PatentProfileWithMatchedTechFeature[] {
        const result: PatentProfileWithMatchedTechFeature[] = [];
        data.forEach((item) => {
            const claimEquivalentInfo = item.claim_equivalent_info || [];
            result.push({
                comparativeLiteratureType: item.comparative_literature_type,
                patentId: item.patent_id,
                mostSimilar: item.most_similar,
                matchedTechFeatures: item.features
                    .map((feature) => ({
                        techFeature: feature.tech_feature,
                        comparisonFeature: feature.comparison_feature,
                        score: feature.score,
                        publicScore: feature.public_score,
                        similar: feature.similar,
                        claimNum: feature.claim_num,
                        claimType: feature.claim_type as ClaimType,
                    })),
                lang: item.lang,
                selected: item.selected,
                comparisonConclusion: item.comparison_conclusion,
                equivalentDependentClaimNum: item.equivalent_dependent_claim_num,
                equivalentIndependentClaimNum: item.equivalent_independent_claim_num,
                dependentClaimNum: item.dependent_claim_num,
                independentClaimNum: item.independent_claim_num,
                riskLevel: item.risk_level as PatentRiskLevel,
                userAdded: item.user_added,
                folderId: item.folder_id,
                workspaceAdded: item.workspace_added,
                claimEquivalentInfo: claimEquivalentInfo.map((info) => ({
                    claimNum: info.claim_num,
                    infringement: info.infringement,
                })),
                claims: item.claims?.map((claim) => ({
                    claimNum: claim.claim_num,
                    claimText: claim.claim_text,
                })),
            });
        });
        return result;
    }
}
